"use client";

import {
  useState,
  type ImgHTMLAttributes,
  type MouseEvent,
  useEffect,
  useRef,
  type SyntheticEvent,
  type FC,
  type Ref,
} from "react";
import { mergeClasses } from "@huub-web/utils";

import type { BackgroundColorProp } from "../../props";
import { Icon, type IconProps } from "../Icon";

import {
  containerStyle,
  imageStyle,
  iconStyle,
  containerBaseStyle,
} from "./Image.css";
import { backgroundColors } from "../../theme/css/colors.css";

export type ImageProps = Omit<
  ImgHTMLAttributes<HTMLImageElement>,
  "src" | "srcSet" | "sizes"
> &
  BackgroundColorProp & {
    /**
     * The image source URL. Overridden from ImgHTMLAttributes to narrow to string (React 19
     * broadened this to string | Blob, but this component only supports URL strings).
     */
    src?: string;
    /**
     * The srcset attribute for responsive images.
     */
    srcSet?: string;
    /**
     * The sizes attribute for responsive images.
     */
    sizes?: string;
    width?: string;
    height?: string;
    borderRadius?: boolean;
    active?: boolean;
    lazy?: boolean;
    placeholder?: boolean;
    fit?: "cover" | "contain";
    filter?: "grayscale" | "none";
    position?: "center" | "top" | "bottom" | "left" | "right";
    icon?: IconProps["icon"];
    forceHover?: boolean;
    outline?: boolean;
    /**
     * Ref forwarded to the root element
     */
    ref?: Ref<HTMLDivElement>;
  };

/**
 * Image component is a wrapper around the img element that provides a container and styling.
 */
export const Image: FC<ImageProps> = ({
  src,
  alt,
  style,
  backgroundColor = "backgroundShade",
  icon = "image",
  borderRadius = false,
  active = false,
  lazy = false,
  srcSet,
  sizes,
  width,
  height,
  placeholder = true,
  fit = "cover",
  filter = "none",
  position = "center",
  forceHover = false,
  outline = false,
  className,
  children,
  onClick,
  onLoad,
  ref,
  ...props
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const currentLoadedSrcRef = useRef<string | undefined>(undefined);

  const isInteractive = !!onClick;

  function handleLoad(event: SyntheticEvent<HTMLImageElement>) {
    const loadedSrc = event.currentTarget.src;
    onLoad?.(event);
    if (src && loadedSrc.endsWith(src)) {
      currentLoadedSrcRef.current = src;
      setIsLoaded(true);
    }
  }

  function handleError() {
    console.warn("[Image] Failed to load:", src);
    setHasError(true);
  }

  useEffect(() => {
    const isDifferentSrc = currentLoadedSrcRef.current !== src;

    if (isDifferentSrc) {
      setHasError(false);

      // Check if image is already complete (cached/SSR case)
      if (
        src &&
        imgRef.current?.complete &&
        imgRef.current.src.endsWith(src) &&
        imgRef.current.naturalWidth > 0
      ) {
        // Image already loaded, sync immediately
        currentLoadedSrcRef.current = src;
        setIsLoaded(true);
      } else {
        // New image needs to load, reset loaded state
        setIsLoaded(false);
      }
    }
  }, [src]);

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: role="button" and tabIndex are conditionally set when onClick is provided
    <div
      ref={ref}
      className={mergeClasses(
        containerBaseStyle,
        containerStyle({
          borderRadius,
          hover: isInteractive,
          outline,
          forceHover,
        }),
        placeholder
          ? backgroundColors[backgroundColor as keyof typeof backgroundColors]
          : undefined,
        className,
      )}
      {...(active ? { "data-active": true } : {})}
      aria-busy={!isLoaded && !hasError}
      style={{ width, height, ...style }}
      onClick={onClick}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick(e as unknown as MouseEvent<HTMLImageElement>);
              }
            }
          : undefined
      }
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {placeholder && (
        <Icon icon={icon} className={iconStyle} color="placeholder" />
      )}
      {src && (
        <img
          ref={imgRef}
          src={src}
          alt={alt}
          srcSet={srcSet}
          sizes={sizes}
          onLoad={handleLoad}
          onError={handleError}
          draggable={false}
          className={mergeClasses(
            imageStyle({
              loading: !isLoaded,
              autoHeight: height === "auto",
              position,
              filter,
              fit,
              hover: isInteractive,
              borderRadius,
              forceHover,
            }),
          )}
          loading={lazy ? "lazy" : "eager"}
          {...props}
        />
      )}
      {children}
    </div>
  );
};
